import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { toast } from "sonner";
import {
  getUserPreferences,
  updateUserPreferences,
  type UserPreferences,
} from "../../lib/auth";

type UserPreferencesContextValue = {
  preferences: UserPreferences;
  isLoading: boolean;
  error: string | null;
  refreshPreferences: () => Promise<void>;
  savePreferences: (
    updates: Partial<Omit<UserPreferences, "userId">>
  ) => Promise<UserPreferences>;
};

const defaultPreferences: UserPreferences = {
  userId: "",
  theme: "dark",
  density: "comfortable",
  lastActiveOrgId: undefined,
};

const UserPreferencesContext = createContext<UserPreferencesContextValue | null>(
  null
);

type UserPreferencesProviderProps = {
  isAuthenticated: boolean;
  children: ReactNode;
};

export function UserPreferencesProvider({
  isAuthenticated,
  children,
}: UserPreferencesProviderProps) {
  const [preferences, setPreferences] =
    useState<UserPreferences>(defaultPreferences);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const saveVersionRef = useRef(0);

  const refreshPreferences = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }
    const refreshStartedAtSaveVersion = saveVersionRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const nextPreferences = await getUserPreferences();
      // Ignore stale refreshes if a save started after this refresh began.
      if (refreshStartedAtSaveVersion !== saveVersionRef.current) {
        return;
      }
      setPreferences(nextPreferences);
    } catch (err) {
      if (refreshStartedAtSaveVersion !== saveVersionRef.current) {
        return;
      }
      const message =
        err instanceof Error ? err.message : "Unable to load preferences.";
      setError(message);
      toast.error(message);
    } finally {
      if (refreshStartedAtSaveVersion === saveVersionRef.current) {
        setIsLoading(false);
      }
    }
  }, [isAuthenticated]);

  const savePreferences = useCallback(
    async (updates: Partial<Omit<UserPreferences, "userId">>) => {
      const saveVersion = saveVersionRef.current + 1;
      saveVersionRef.current = saveVersion;
      const previousPreferences = preferences;
      const optimisticPreferences: UserPreferences = {
        ...previousPreferences,
        ...updates,
      };
      setIsLoading(true);
      setError(null);
      setPreferences(optimisticPreferences);
      try {
        const nextPreferences = await updateUserPreferences(updates);
        if (saveVersion !== saveVersionRef.current) {
          return nextPreferences;
        }
        const mergedPreferences: UserPreferences = {
          ...nextPreferences,
          theme: optimisticPreferences.theme,
        };
        setPreferences(mergedPreferences);
        return mergedPreferences;
      } catch (err) {
        if (saveVersion !== saveVersionRef.current) {
          return optimisticPreferences;
        }
        setPreferences(previousPreferences);
        const message =
          err instanceof Error ? err.message : "Unable to save preferences.";
        setError(message);
        toast.error(message);
        throw err;
      } finally {
        if (saveVersion === saveVersionRef.current) {
          setIsLoading(false);
        }
      }
    },
    [preferences]
  );

  useEffect(() => {
    if (!isAuthenticated) {
      setPreferences(defaultPreferences);
      setError(null);
      delete document.documentElement.dataset.density;
      return;
    }
    refreshPreferences();
  }, [isAuthenticated, refreshPreferences]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    document.documentElement.dataset.density = preferences.density;
    return () => {
      delete document.documentElement.dataset.density;
    };
  }, [isAuthenticated, preferences.density]);

  const value = useMemo<UserPreferencesContextValue>(
    () => ({
      preferences,
      isLoading,
      error,
      refreshPreferences,
      savePreferences,
    }),
    [preferences, isLoading, error, refreshPreferences, savePreferences]
  );

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  );
}

export function useUserPreferences() {
  const context = useContext(UserPreferencesContext);
  if (!context) {
    throw new Error("useUserPreferences must be used within UserPreferencesProvider.");
  }
  return context;
}
